// ui/modules/legal/forms/ContractRepoForm.tsx
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import type { AttachmentMeta } from "../../../types/attachments";
import { DateField } from "../../../components/form/DateField";
import { formatDate, parseDate } from "../../../utils/dateUtils";
import { fetchNextContractNo } from "../../../utils/idGenerator";
import { useAuth } from "../../../context/AuthContext";
import { isSuperUser, getDeptCodeByEmail } from "../../../config/roles";
import { CONTRACT_TYPE_OPTIONS } from "../../../constants/contractTypes";
import { SystemLinkagePanel } from "../../../components/shared/SystemLinkagePanel";
import { DEPARTMENTS } from "../../../constants/departments";

import { analyzeUploadedFile } from "../../../services/aiService";
import { useToast } from "../../../components/Toast";

import { storage } from "../../../../firebaseClient";

interface Props {
  initialData: any | null;
  isLegacy: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}

const safeArray = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

function normalizeContractStatus(raw: any): "NOT_EFFECTIVE" | "ACTIVE" | "EXPIRED" | "TERMINATED" {
  if (!raw) return "ACTIVE";
  const s = String(raw).trim();
  const u = s.toUpperCase();

  // 中文狀態
  if (s.includes("尚未生效") || s.includes("未生效") || s.includes("草稿")) return "NOT_EFFECTIVE";
  if (s.includes("生效中") || s.includes("有效")) return "ACTIVE";
  if (s.includes("已屆滿") || s.includes("已到期")) return "EXPIRED";
  if (s.includes("已解約") || s.includes("已終止")) return "TERMINATED";

  // 代碼狀態（兼容舊的 DRAFT）
  if (u === "NOT_EFFECTIVE" || u === "DRAFT") return "NOT_EFFECTIVE";
  if (u === "ACTIVE") return "ACTIVE";
  if (u === "EXPIRED") return "EXPIRED";
  if (u === "TERMINATED") return "TERMINATED";

  return "ACTIVE";
}

function normalizeDeptCode(input: string): string {
  const upper = (input || "").toUpperCase().trim();
  // 舊值相容（避免存進去後被 dept isolation 擋掉）
  const MAP: Record<string, string> = {
    LEGAL: "LG",
    HR: "AP",
    MKT: "MK",
    PUR: "PT",
  };
  return MAP[upper] || upper;
}

async function uploadFilesToRepo(basePath: string, docKey: string, files: File[]): Promise<AttachmentMeta[]> {
  const metas: AttachmentMeta[] = [];
  for (const file of files) {
    const ts = Date.now();
    const safeName = file.name.replace(/\s+/g, "_");
    const fullPath = `${basePath}/${docKey}/${ts}-${safeName}`;
    const ref = storage.ref(fullPath);
    const snap = await ref.put(file);
    const url = await snap.ref.getDownloadURL();

    metas.push({
      name: file.name,
      url,
      path: fullPath,
      contentType: file.type,
      size: file.size,
      uploadedAt: new Date().toISOString(),
    });
  }
  return metas;
}

export function ContractRepoForm({ initialData, isLegacy, onClose, onSave }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const inferredDept = normalizeDeptCode(getDeptCodeByEmail(user?.email) || "");
  const deptLocked = !!user?.email && !isSuperUser(user.email);

  const [form, setForm] = useState<any>(() => {
    if (initialData) {
      return {
        ...initialData,
        dept: normalizeDeptCode(initialData.dept || ""),
        status: normalizeContractStatus((initialData as any).status),
        currency: (initialData as any).currency || "TWD",
        totalAmount: (initialData as any).totalAmount ?? undefined,
      };
    }
    return {
      title: "",
      contractType: "business-general",
      counterparty: "",
      signedDate: formatDate(new Date()),
      effectiveDate: "",
      expiryDate: "",
      milestones: [],
      summary: "",
      status: "ACTIVE",
      currency: "TWD",
      totalAmount: undefined,
      attachments: [],
      relatedNos: {},
      dept: inferredDept || "",
    };
  });

  const [rawFiles, setRawFiles] = useState<File[]>([]);

  async function handleAiAutoFill() {
    if (aiLoading) return;

    try {
      setAiLoading(true);

      const ctx = {
        number: form?.number,
        title: form?.title,
        contractType: form?.contractType,
        counterparty: form?.counterparty,
        dept: form?.dept,
        signedDate: form?.signedDate,
        expiryDate: form?.expiryDate,
        summary: form?.summary,
        currency: form?.currency,
        totalAmount: form?.totalAmount,
      };

      // 若使用者沒有重新上傳檔案（rawFiles 空），就改用已儲存的附件做解析
      const existingFileMeta = safeArray<AttachmentMeta>(form.attachments)?.[0] || null;

      const res = await analyzeUploadedFile({
        mode: "extractFields",
        contextType: isLegacy ? "LEGACY_CONTRACT" : "CONTRACT_REPO",
        contextId: form?.id || form?.number || "new",
        rawFiles,
        fileMeta: existingFileMeta,
        userInstruction:
          "請根據上傳檔案與目前表單欄位，幫我提出：1) 合約摘要；2) 合約總價額(若可辨識)；3) 可能的簽約日期/到期日(若可辨識)。\n\n目前表單：" +
          JSON.stringify(ctx, null, 2),
      });

      setForm((prev: any) => {
        const next = { ...prev };

        if (!next.summary) next.summary = res.summary;
        else if (res.summary) next.summary = (next.summary + "\n" + res.summary).trim();

        const fields: any = (res as any)?.fields || (res as any)?.extractedFields || {};

        if (!next.title && fields.title) next.title = fields.title;
        if (!next.counterparty) {
          const cp = fields.counterparty || fields.partyB || fields.vendor || fields.customer;
          if (cp) next.counterparty = String(cp);
        }

        // Amount (保守回填：只在欄位空/0時填入)
        const curAmtNum = Number(next.totalAmount || 0);
        const canFillAmount = !next.totalAmount || curAmtNum === 0;
        if (canFillAmount) {
          const rawCandidate =
            fields.amountValue ??
            fields.totalAmount?.value ??
            fields.totalAmountValue ??
            fields.amount ??
            fields.totalAmount;

          const candidateNum =
            typeof rawCandidate === "number"
              ? rawCandidate
              : typeof rawCandidate === "string"
              ? Number(rawCandidate.replace(/,/g, "").replace(/[^0-9.]/g, ""))
              : NaN;

          if (Number.isFinite(candidateNum) && candidateNum > 0) {
            next.totalAmount = candidateNum;
          }
        }

        if (!next.currency) {
          const c = fields.totalAmount?.currency || fields.amountCurrency || fields.currency;
          if (c) next.currency = String(c);
        }

        // Dates（保守回填）
        if (!next.signedDate) {
          const d = fields.term?.signDate || fields.mainDate || fields.date || fields.effectiveDate;
          if (d) next.signedDate = String(d);
        }
        if (!next.effectiveDate) {
          const d = fields.term?.periodStart || fields.effectiveDate || fields.mainDate;
          if (d) next.effectiveDate = String(d);
        }
        if (!next.expiryDate) {
          const d = fields.term?.periodEnd || fields.expiryDate;
          if (d) next.expiryDate = String(d);
        }

        return next;
      });

      showToast("AI 已回填草稿", "success");
    } catch (err: any) {
      console.error(err);
      showToast(err?.message || "AI 產生失敗", "error");
    } finally {
      setAiLoading(false);
    }
  }

  // 使用者資訊較晚才出現：補 dept（新增時）＋非超管鎖定 dept
  useEffect(() => {
    if (!user) return;

    if (!initialData) {
      if (!form?.dept && inferredDept) {
        setForm((p: any) => ({ ...p, dept: inferredDept }));
      }
      // 非超管：若手動改了 dept，直接拉回推導值（避免存錯後自己看不到）
      if (deptLocked && inferredDept && form?.dept && normalizeDeptCode(form.dept) !== inferredDept) {
        setForm((p: any) => ({ ...p, dept: inferredDept }));
      }
    } else {
      // 編輯：非超管也不允許改到其他部門
      if (deptLocked && inferredDept && normalizeDeptCode(form.dept) !== inferredDept) {
        setForm((p: any) => ({ ...p, dept: inferredDept }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, initialData]);

  useEffect(() => {
    if (!initialData && !form.contractNo && !form.number) {
      fetchNextContractNo(isLegacy).then((no) =>
        setForm((p: any) => ({
          ...p,
          contractNo: no,
          number: no, // ✅ 兼容清單頁用 item.number 顯示
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isLegacy]);

  const handleAddMilestone = () => {
    setForm((p: any) => ({
      ...p,
      milestones: [...(p.milestones || []), { label: "", date: formatDate(new Date()) }],
    }));
  };

  const handleRemoveRemoteFile = async (file: AttachmentMeta, index: number) => {
    const current = safeArray<AttachmentMeta>(form.attachments);
    const next = current.filter((_, i) => i !== index);
    setForm((p: any) => ({ ...p, attachments: next }));

    const path = (file as any)?.path || (file as any)?.storagePath;
    if (path) {
      try {
        await storage.ref(path).delete();
      } catch (e) {
        console.warn("[ContractRepo] delete storage failed:", e);
      }
    }
  };

  const handleSave = async () => {
    const finalDept = deptLocked
      ? inferredDept
      : normalizeDeptCode((form.dept || inferredDept || "").toString());

    if (!form.title || !form.contractType || !form.counterparty || !form.effectiveDate || !finalDept) {
      const missing: string[] = [];
      if (!form.title) missing.push("合約名稱");
      if (!form.contractType) missing.push("合約類型");
      if (!form.counterparty) missing.push("相對人(對造)");
      if (!form.effectiveDate) missing.push("生效日");
      if (!finalDept) missing.push("部門");
      return alert("請填寫必填欄位: " + missing.join(", "));
    }

    const docKey =
      form.contractNo ||
      form.number ||
      initialData?.contractNo ||
      initialData?.number;

    if (!docKey) return alert("合約編號尚未產生完成，請稍等 1 秒再試一次。");

    setLoading(true);
    try {
      const basePath = isLegacy ? "legacy-contracts" : "legal-contracts";
      let attachments = safeArray<AttachmentMeta>(form.attachments);

      if (rawFiles.length > 0) {
        const uploaded = await uploadFilesToRepo(basePath, docKey, rawFiles);
        attachments = [...attachments, ...uploaded];
      }

      const payload = {
        ...form,
        status: normalizeContractStatus(form.status),
        attachments,

        // ✅ 關鍵：dept 一律存「部門代碼」(GM/FA/AP/IT/LG/…)
        dept: finalDept,

        // ✅ 兼容：清單頁使用 number 顯示合約編號
        number: form.number || form.contractNo || docKey,
        contractNo: form.contractNo || form.number || docKey,

        ownerUid: (user as any)?.id || (user as any)?.uid || "unknown",
        isLegacy,
      };

      await onSave(payload);
      onClose();
    } catch (e: any) {
      console.error(e);
      alert("儲存/上傳失敗: " + (e?.message || "unknown"));
    } finally {
      setLoading(false);
      setRawFiles([]);
    }
  };

  const deptKnown = DEPARTMENTS.some((d) => d.code === normalizeDeptCode(form.dept));

  return (
    <Modal
      title={(isLegacy ? "舊合約登錄" : "新增合約") + (initialData ? " - 編輯" : " - 新增")}
      onClose={onClose}
      width={850}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="field">
            <label className="label">合約名稱 *</label>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="請輸入合約標題"
            />
          </div>

          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="field">
              <label className="label">合約編號</label>
              <input
                className="input"
                value={form.contractNo || form.number || "序號產生中..."}
                readOnly
                style={{ background: "#111", color: "#fbbf24", fontFamily: "monospace" }}
              />
            </div>

            <div className="field">
              <label className="label">合約類型 *</label>
              <select
                className="input"
                value={form.contractType}
                onChange={(e) => setForm({ ...form, contractType: e.target.value })}
              >
                <option value="">請選擇</option>
                {CONTRACT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label">相對人 (對造) *</label>
              <input
                className="input"
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                placeholder="公司全銜"
              />
            </div>

            {/* ✅ 部門下拉：使用 DEPARTMENTS 的 code (GM/FA/AP/IT/LG/...) */}
            <div className="field">
              <label className="label">部門 *</label>
              <select
                className="input"
                value={normalizeDeptCode(form.dept || "")}
                onChange={(e) => setForm({ ...form, dept: e.target.value })}
                disabled={deptLocked}
              >
                <option value="">請選擇</option>
                {!deptKnown && form.dept ? (
                  <option value={form.dept}>{`(未登錄) ${form.dept}`}</option>
                ) : null}
                {DEPARTMENTS.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label">目前狀態</label>
              <select
                className="input"
                value={normalizeContractStatus(form.status)}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="NOT_EFFECTIVE">尚未生效</option>
                <option value="ACTIVE">生效中</option>
                <option value="EXPIRED">已屆滿</option>
                <option value="TERMINATED">已解約</option>
              </select>
            </div>

            <div className="field">
              <label className="label">合約總價額</label>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <select
                  className="input"
                  style={{ width: 140 }}
                  value={(form as any).currency || "TWD"}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                >
                  <option value="TWD">新台幣</option>
                  <option value="USD">美金</option>
                  <option value="JPY">日幣</option>
                  <option value="KRW">韓圜</option>
                  <option value="OTHER">其他</option>
                </select>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  type="number"
                  inputMode="decimal"
                  value={(form as any).totalAmount ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!raw) {
                      setForm({ ...form, totalAmount: undefined });
                      return;
                    }
                    const n = Number(raw);
                    setForm({ ...form, totalAmount: Number.isFinite(n) ? n : undefined });
                  }}
                  placeholder="例如：1500000"
                />
              </div>
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <DateField
              label="生效日期 (必填) *"
              value={parseDate(form.effectiveDate || null)}
              onChange={(d) => setForm({ ...form, effectiveDate: formatDate(d) })}
            />
            <DateField
              label="到期日期"
              value={parseDate(form.expiryDate || null)}
              onChange={(d) => setForm({ ...form, expiryDate: formatDate(d) })}
            />
          </div>

          <div className="field">
            <label className="label">內容摘要</label>
            <textarea
              className="input"
              rows={6}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="請填寫合約核心重點義務與風險提示..."
            />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <SystemLinkagePanel
            relatedNos={form.relatedNos}
            linkKey={form.linkKey}
            onChange={({ relatedNos, linkKey }) => setForm({ ...form, relatedNos, linkKey })}
          />

          <div
            className="section"
            style={{
              background: "rgba(255,255,255,0.02)",
              padding: 12,
              borderRadius: 12,
              border: "1px solid #333",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <label className="label" style={{ margin: 0, fontWeight: "bold" }}>
                履約里程碑
              </label>
              <button
                className="smallbtn"
                style={{ fontSize: 10, padding: "2px 6px" }}
                onClick={handleAddMilestone}
                type="button"
              >
                + 增加點位
              </button>
            </div>

            <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {form.milestones?.map((m: any, i: number) => (
                <div key={i} style={{ display: "flex", gap: 6, background: "#222", padding: 6, borderRadius: 6 }}>
                  <input
                    type="date"
                    className="input"
                    style={{ width: 120, padding: "4px 8px", fontSize: 12 }}
                    value={m.date}
                    onChange={(e) => {
                      const next = [...form.milestones];
                      next[i].date = e.target.value;
                      setForm({ ...form, milestones: next });
                    }}
                  />
                  <input
                    className="input"
                    style={{ flex: 1, padding: "4px 8px", fontSize: 12 }}
                    placeholder="標籤(例:付二期款)"
                    value={m.label}
                    onChange={(e) => {
                      const next = [...form.milestones];
                      next[i].label = e.target.value;
                      setForm({ ...form, milestones: next });
                    }}
                  />
                </div>
              ))}
              {(form.milestones?.length || 0) === 0 && (
                <div className="p" style={{ textAlign: "center", padding: 10, fontSize: 12, color: "#555" }}>
                  無設定里程碑
                </div>
              )}
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", right: 0, top: -6, zIndex: 2 }}>
              <button
                type="button"
                onClick={handleAiAutoFill}
                disabled={aiLoading}
                title={aiLoading ? "AI 產生中..." : "AI 自動回填（草擬）"}
                className="btn"
                style={{
                  width: "auto",
                  padding: "6px 10px",
                  background: "linear-gradient(90deg, #9b5cff, #ff3ea5)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 999,
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: aiLoading ? 0.7 : 1,
                  cursor: aiLoading ? "not-allowed" : "pointer",
                }}
              >
                ✨ AI 自動回填
              </button>
            </div>
            <FileUploadKit
              label="合約掃描檔/附件"
              existingFiles={safeArray<AttachmentMeta>(form.attachments)}
              rawFiles={rawFiles}
              onFilesSelected={(files) => setRawFiles((p) => [...p, ...files])}
              onRemoveRawFile={(idx) => setRawFiles((p) => p.filter((_, i) => i !== idx))}
              onRemoveFile={handleRemoveRemoteFile}
            />
          </div>
        </div>
      </div>

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 12 }}>
        <button
          className="btn"
          style={{ width: "auto", background: "transparent", border: "1px solid #555", color: "#ccc" }}
          onClick={onClose}
          disabled={loading}
        >
          取消
        </button>
        <button className="btn" style={{ width: "auto" }} onClick={handleSave} disabled={loading}>
          {loading ? "處理中..." : "確認存檔"}
        </button>
      </div>
    </Modal>
  );
}
